/**
 * Non-blocking, throttled printer-readiness probe.
 *
 * Verifies that the saved/active printer is still reachable and healthy and
 * pushes the result into `usePrinterStore` (driving the Footer V4 indicator).
 * It never opens a modal, never interrupts checkout, and never discovers
 * printers — it only reads status (which reconnects headlessly via the printer
 * lock if needed) and updates state.
 *
 * Safety:
 *   - Gated on `isPrintingEnabled` — does nothing for businesses without printing.
 *   - Throttled (15s) and single-flight, so repeated triggers (mount, app resume,
 *     status events) can't spin up a connection loop. Pass `{ force: true }` to
 *     bypass the throttle for explicit refreshes (startup, post-selection).
 */
import { usePrinterStore } from 'src/store/PrinterStore';
import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { starPrintService, isBluetoothInterface } from 'src/services/printers';

import { isPrintingEnabled } from './printing-access';

const THROTTLE_MS = 15_000;

let lastCheckAt = 0;
let inFlight: Promise<void> | null = null;

export async function checkPrinterReadiness(opts?: {
  force?: boolean;
  /**
   * Tear down and rebuild the connection before reading status. Needed after a
   * real drop: the native layer can hold a half-open handle whose status reads
   * report offline forever without ever throwing (so the reconnect-once-on-stale
   * path never triggers). Used by the background auto-reconnect loop.
   */
  reconnect?: boolean;
}): Promise<void> {
  // Never probe hardware for businesses that don't have printing enabled.
  if (!isPrintingEnabled(useDeviceSyncStore.getState().businessData)) return;

  const { activePrinter, applyStatus, setIsPrinterReady, markPrinterUnreachable } =
    usePrinterStore.getState();
  // No printer chosen yet → not ready; the footer prompts the user to pick one.
  if (!activePrinter) {
    setIsPrinterReady(false);
    return;
  }

  // Bluetooth printers: read OS Bluetooth authorization SILENTLY first
  // (CBManager.authorization on iOS — never creates a CBCentralManager, so no
  // permission prompt). Only proceed to the real BT connect when already
  // authorized; otherwise leave the printer "not ready" so the footer surfaces
  // the help button. LAN/USB skip this entirely and verify over the network/USB.
  if (isBluetoothInterface(activePrinter.interfaceType)) {
    try {
      const { status } = await starPrintService.getBluetoothAuthorization();
      if (status !== 'authorized') {
        markPrinterUnreachable();
        return;
      }
    } catch (error) {
      console.warn('[printer-readiness] bluetooth authorization check failed:', error);
      markPrinterUnreachable();
      return;
    }
  }

  const now = Date.now();
  if (!opts?.force && now - lastCheckAt < THROTTLE_MS) return;
  // A check is already running — wait for it rather than starting another.
  if (inFlight) {
    await inFlight;
    return;
  }

  lastCheckAt = now;
  inFlight = (async () => {
    try {
      const status = await starPrintService.getPrinterStatus({
        forceReconnect: opts?.reconnect === true,
      });
      applyStatus(status);
    } catch (error) {
      console.warn('[printer-readiness] status check failed:', error);
      markPrinterUnreachable();
    } finally {
      inFlight = null;
    }
  })();

  await inFlight;
}
